/**
 * deathEffect — the timeline for a player's death beat.
 *
 * When a player dies their sprite group is not destroyed on the spot; it is
 * handed to PixiEntityRenderer's dying list and played through this timeline:
 * a white hit-flash, a quick "pop" then shrink-and-spin fade of the body, an
 * expanding shockwave ring and a burst of shards. Procedural stand-in until the
 * art pass (see docs/ART_DEBT.md).
 *
 * Kept free of pixi imports so it can be unit tested; the renderer applies the
 * returned frame to real display objects.
 */

export const DEATH_EFFECT_DURATION_MS = 700;
export const DEATH_SHARD_COUNT = 12;

const POP_END = 0.12;      // body swells to POP_SCALE by here...
const POP_SCALE = 1.2;
const END_SCALE = 0.25;    // ...then shrinks to END_SCALE by the end
const FLASH_END = 0.3;
const SPIN_RADIANS = 1.2;
const RING_START_RADIUS = 40;
const RING_END_RADIUS = 120;
const SHARD_DISTANCE = 95;

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInQuad = (t) => t * t;

/**
 * @param {number} elapsedMs time since the death beat started
 * @returns {{done: boolean, bodyScale: number, bodyAlpha: number, spin: number,
 *   flashAlpha: number, ringRadius: number, ringAlpha: number,
 *   shardDistance: number, shardAlpha: number, shardSize: number}}
 */
export function deathEffectFrame(elapsedMs) {
  const t = clamp01(elapsedMs / DEATH_EFFECT_DURATION_MS);
  const out = easeOutCubic(t);

  const bodyScale = t < POP_END
    ? 1 + (POP_SCALE - 1) * (t / POP_END)
    : POP_SCALE - (POP_SCALE - END_SCALE) * easeInQuad((t - POP_END) / (1 - POP_END));

  return {
    done: elapsedMs >= DEATH_EFFECT_DURATION_MS,
    bodyScale,
    bodyAlpha: 1 - easeInQuad(t),
    spin: SPIN_RADIANS * out,
    flashAlpha: t < FLASH_END ? 0.85 * (1 - t / FLASH_END) : 0,
    ringRadius: RING_START_RADIUS + (RING_END_RADIUS - RING_START_RADIUS) * out,
    ringAlpha: 0.8 * (1 - t),
    shardDistance: SHARD_DISTANCE * out,
    shardAlpha: 1 - t,
    shardSize: 8 - 5 * t
  };
}

/**
 * Evenly spread shard directions with a little jitter so every death does not
 * look identical. `random` is injectable for tests.
 * @returns {{angle: number, speed: number}[]} speed is a 0.6..1.0 multiplier on distance
 */
export function makeDeathShards(count = DEATH_SHARD_COUNT, random = Math.random) {
  const step = (Math.PI * 2) / count;
  const offset = random() * step;
  const shards = [];
  for (let i = 0; i < count; i++) {
    shards.push({
      angle: offset + i * step + (random() - 0.5) * step * 0.6,
      speed: 0.6 + random() * 0.4
    });
  }
  return shards;
}
